import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { ArrowRight, ArrowLeft, CheckCircle2, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const schema = z.object({
  firstName: z.string().min(2, 'Vorname ist erforderlich'),
  lastName: z.string().min(2, 'Nachname ist erforderlich'),
  email: z.string().email('Ungültige E-Mail-Adresse'),
  phone: z.string().optional(),
  company: z.string().min(2, 'Unternehmen ist erforderlich'),
  services: z.array(z.string()).min(1, 'Bitte wählen Sie mindestens eine Leistung'),
  message: z.string().min(10, 'Bitte beschreiben Sie Ihr Anliegen'),
  privacy: z.boolean().refine(val => val === true, 'Bitte akzeptieren Sie die Datenschutzerklärung')
});

const serviceOptions = [
  { id: 'gruendung', label: 'Bildungsträger gründen' },
  { id: 'zulassung', label: 'Träger-/Maßnahmezulassung' },
  { id: 'audit', label: 'Auditvorbereitung' },
  { id: 'internes-audit', label: 'Internes Audit' },
  { id: 'qm-beauftragter', label: 'Externer QM-Beauftragter' },
  { id: 'musterunterlagen', label: 'Musterunterlagen & QM-System' }
];

export const ContactForm = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    formState: { errors },
    trigger
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      services: [],
      message: '',
      privacy: false
    }
  });

  const selectedServices = useWatch({ control, name: 'services' }) || [];
  const privacyValue = useWatch({ control, name: 'privacy' });

  const handleServiceToggle = useCallback((serviceId) => {
    const current = getValues('services') || [];
    const updated = current.includes(serviceId)
      ? current.filter(id => id !== serviceId)
      : [...current, serviceId];
    setValue('services', updated, { shouldValidate: false });
  }, [getValues, setValue]);

  const nextStep = async () => {
    let fieldsToValidate = [];
    if (step === 1) fieldsToValidate = ['firstName', 'lastName', 'email', 'company'];
    if (step === 2) fieldsToValidate = ['services'];
    
    const isValid = await trigger(fieldsToValidate);
    if (isValid) setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, data);
      setIsSuccess(true);
      toast.success('Ihre Anfrage wurde erfolgreich gesendet!');
    } catch (error) {
      toast.error('Fehler beim Senden. Bitte versuchen Sie es erneut.');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12"
        data-testid="contact-success"
      >
        <div className="w-20 h-20 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-10 h-10 text-green-500" />
        </div>
        <h3 className="text-2xl font-heading font-semibold text-white mb-4">Vielen Dank!</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          Ihre Anfrage wurde erfolgreich gesendet. Wir melden uns innerhalb von 24 Stunden bei Ihnen.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="contact-form">
      {/* Progress indicator */}
      <div className="flex items-center justify-between mb-8" data-testid="form-progress">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-heading font-semibold text-sm transition-colors ${
                s <= step ? 'bg-primary text-white' : 'bg-white/5 text-muted-foreground border border-white/10'
              }`}
            >
              {s < step ? <CheckCircle2 size={18} /> : s}
            </div>
            {s < 3 && (
              <div className={`w-16 md:w-24 h-[2px] mx-2 transition-colors ${s < step ? 'bg-primary' : 'bg-white/10'}`} />
            )}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* Step 1: Contact Info */}
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
            data-testid="form-step-1"
          >
            <div className="mb-6">
              <h3 className="text-xl font-heading font-semibold text-white mb-2">Kontaktdaten</h3>
              <p className="text-sm text-muted-foreground">Wie können wir Sie erreichen?</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-white">Vorname *</Label>
                <Input
                  id="firstName"
                  {...register('firstName')}
                  className="bg-secondary/50 border-white/10 focus:border-primary h-12 text-white placeholder:text-neutral-500"
                  placeholder="Max"
                  data-testid="input-firstName"
                />
                {errors.firstName && <p className="text-red-500 text-sm">{errors.firstName.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-white">Nachname *</Label>
                <Input
                  id="lastName"
                  {...register('lastName')}
                  className="bg-secondary/50 border-white/10 focus:border-primary h-12 text-white placeholder:text-neutral-500"
                  placeholder="Mustermann"
                  data-testid="input-lastName"
                />
                {errors.lastName && <p className="text-red-500 text-sm">{errors.lastName.message}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">E-Mail *</Label>
              <Input
                id="email"
                type="email"
                {...register('email')}
                className="bg-secondary/50 border-white/10 focus:border-primary h-12 text-white placeholder:text-neutral-500"
                placeholder="max@beispiel.de"
                data-testid="input-email"
              />
              {errors.email && <p className="text-red-500 text-sm">{errors.email.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-white">Telefon (optional)</Label>
              <Input
                id="phone"
                type="tel"
                {...register('phone')}
                className="bg-secondary/50 border-white/10 focus:border-primary h-12 text-white placeholder:text-neutral-500"
                placeholder="+49 123 456789"
                data-testid="input-phone"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company" className="text-white">Unternehmen *</Label>
              <Input
                id="company"
                {...register('company')}
                className="bg-secondary/50 border-white/10 focus:border-primary h-12 text-white placeholder:text-neutral-500"
                placeholder="Muster GmbH"
                data-testid="input-company"
              />
              {errors.company && <p className="text-red-500 text-sm">{errors.company.message}</p>}
            </div>
          </motion.div>
        )}

        {/* Step 2: Services */}
        {step === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
            data-testid="form-step-2"
          >
            <div className="mb-6">
              <h3 className="text-xl font-heading font-semibold text-white mb-2">Gewünschte Leistungen</h3>
              <p className="text-sm text-muted-foreground">Wofür interessieren Sie sich?</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {serviceOptions.map((service) => {
                const isSelected = selectedServices.includes(service.id);
                return (
                  <div
                    key={service.id}
                    onClick={() => handleServiceToggle(service.id)}
                    className={`p-4 rounded-xl border cursor-pointer transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/10'
                        : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                    }`}
                    data-testid={`service-option-${service.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                        isSelected 
                          ? 'bg-primary border-primary' 
                          : 'border-white/20 bg-transparent'
                      }`}>
                        {isSelected && <Check size={14} className="text-white" />}
                      </div>
                      <span className="text-white font-medium">{service.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            {errors.services && <p className="text-red-500 text-sm">{errors.services.message}</p>}
          </motion.div>
        )}

        {/* Step 3: Message */}
        {step === 3 && (
          <motion.div
            key="step3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
            data-testid="form-step-3"
          >
            <div className="mb-6">
              <h3 className="text-xl font-heading font-semibold text-white mb-2">Ihre Nachricht</h3>
              <p className="text-sm text-muted-foreground">Erzählen Sie uns mehr über Ihr Anliegen.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-white">Nachricht *</Label>
              <Textarea
                id="message"
                {...register('message')}
                className="bg-secondary/50 border-white/10 focus:border-primary min-h-[150px] text-white placeholder:text-neutral-500"
                placeholder="Beschreiben Sie Ihr Anliegen..."
                data-testid="input-message"
              />
              {errors.message && <p className="text-red-500 text-sm">{errors.message.message}</p>}
            </div>

            <div className="flex items-start gap-3">
              <div 
                onClick={() => setValue('privacy', !privacyValue, { shouldValidate: true })}
                className={`w-5 h-5 rounded border flex items-center justify-center cursor-pointer transition-colors mt-0.5 flex-shrink-0 ${
                  privacyValue 
                    ? 'bg-primary border-primary' 
                    : 'border-white/20 bg-transparent'
                }`}
                data-testid="input-privacy"
              >
                {privacyValue && <Check size={14} className="text-white" />}
              </div>
              <Label htmlFor="privacy" className="text-sm text-muted-foreground leading-relaxed cursor-pointer" onClick={() => setValue('privacy', !privacyValue, { shouldValidate: true })}>
                Ich habe die Datenschutzerklärung gelesen und akzeptiere diese. *
              </Label>
            </div>
            {errors.privacy && <p className="text-red-500 text-sm">{errors.privacy.message}</p>}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        {step > 1 ? (
          <Button
            type="button"
            onClick={prevStep}
            variant="outline"
            className="bg-transparent border border-white/20 hover:bg-white/10 text-white rounded-lg px-6 py-5 transition-all"
            data-testid="form-prev-button"
          >
            <ArrowLeft size={18} className="mr-2" />
            Zurück
          </Button>
        ) : (
          <div />
        )}

        {step < 3 ? (
          <Button
            type="button"
            onClick={nextStep}
            className="bg-primary hover:bg-[#05b873] text-black font-semibold rounded-lg px-6 py-5 transition-all"
            data-testid="form-next-button"
          >
            Weiter
            <ArrowRight size={18} className="ml-2" />
          </Button>
        ) : (
          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-primary hover:bg-[#05b873] text-black font-semibold rounded-lg px-8 py-5 transition-all"
            data-testid="form-submit-button"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={18} className="mr-2 animate-spin" />
                Wird gesendet...
              </>
            ) : (
              <>
                Anfrage senden
                <ArrowRight size={18} className="ml-2" />
              </>
            )}
          </Button>
        )}
      </div>
    </form>
  );
};
